// src/components/common/LoadingSpinner.tsx

import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { Favorite as HeartIcon } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

type ColorConfig = {
  light?: string;
  dark?: string;
};

type ColorProp = string | ColorConfig;

interface LoadingSpinnerProps {
  message?: string;
  size?: number;
  minHeight?: string;
  compact?: boolean;
  color?: ColorProp;
  spinnerSize?: number;
  gap?: string;
}

const LoadingContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{
  minHeight?: string;
  compact?: boolean;
}>(({ minHeight, compact }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: minHeight || (compact ? '120px' : '400px'),
  flexDirection: 'column',
  gap: '1rem',
  padding: '2rem',
  height: compact ? 'auto' : '50vh',

  '@media (max-width: 600px)': {
    minHeight: minHeight
      ? `calc(${minHeight} * 0.6)`
      : compact
        ? '100px'
        : '250px',
    gap: '0.75rem',
    padding: compact ? '1rem' : '1.5rem',
    height: compact ? 'auto' : '50vh',
  },

  '@media (max-width: 400px)': {
    minHeight: minHeight
      ? `calc(${minHeight} * 0.5)`
      : compact
        ? '80px'
        : '200px',
    gap: '0.5rem',
    padding: compact ? '0.75rem' : '1rem',
    height: compact ? 'auto' : '50vh',
  },
}));

const LoadingText = styled(Typography)<{ color?: string }>(
  ({ theme, color }) => ({
    fontSize: '1.8rem',
    fontWeight: 700,
    color:
      color ||
      (theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.main),
    marginTop: '1rem',
  })
);

const SpinnerContainer = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const heartPulse = keyframes`
  0% {
    transform: scale(0.2);
    opacity: 0.8;
  }
  92% {
    opacity: 0;
  }
  100% {
    transform: scale(4.8);
    opacity: 0;
  }
`;

const HeartPulseWrapper = styled('span')({
  position: 'absolute',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${heartPulse} 1.25s ease-out infinite`,
  willChange: 'transform, opacity',
});

const resolveColor = (colorProp: ColorProp | undefined, theme: any): string => {
  if (!colorProp) {
    return theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.main;
  }

  if (typeof colorProp === 'string') {
    return colorProp;
  }

  const isDarkMode = theme.palette.mode === 'dark';
  if (isDarkMode && colorProp.dark) {
    return colorProp.dark;
  }
  if (!isDarkMode && colorProp.light) {
    return colorProp.light;
  }

  return (
    colorProp.light ||
    colorProp.dark ||
    (isDarkMode ? theme.palette.primary.light : theme.palette.primary.main)
  );
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  size = 40,
  minHeight,
  compact = false,
  color,
  spinnerSize,
  gap,
}) => {
  const theme = useTheme();

  const responsiveSize =
    typeof window !== 'undefined' && window.innerWidth < 600
      ? Math.max((spinnerSize || size) * 0.8, 30)
      : spinnerSize || size;

  const resolvedColor = resolveColor(color, theme);
  const gapValue = gap || '1rem';

  const defaultMessage = compact ? undefined : 'Loading...';
  const displayMessage = message !== undefined ? message : defaultMessage;
  // Passing message="" explicitly suppresses the text; omitting message
  // entirely falls back to defaultMessage and should still show it.
  const shouldShowMessage =
    displayMessage !== undefined && displayMessage !== '';

  return (
    <LoadingContainer
      minHeight={minHeight}
      compact={compact}
      sx={{ gap: gapValue }}
    >
      <SpinnerContainer>
        <CircularProgress
          disableShrink
          size={responsiveSize}
          sx={{
            color: resolvedColor,
          }}
        />
        <HeartPulseWrapper>
          <HeartIcon
            style={{ fontSize: responsiveSize * 0.4, color: resolvedColor }}
          />
        </HeartPulseWrapper>
      </SpinnerContainer>
      {shouldShowMessage && displayMessage && (
        <LoadingText color={resolvedColor}>{displayMessage}</LoadingText>
      )}
    </LoadingContainer>
  );
};

export default LoadingSpinner;
